import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Square, RefreshCw, Loader2, Timer, TrendingUp, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const fmt = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
const fmtClock = (sec) => {
  const h = String(Math.floor(sec / 3600)).padStart(2, "0");
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export default function TimeTracker() {
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useState(null);
  const [today, setToday] = useState({ sessions: [], total_min: 0, by_project: [] });
  const [prod, setProd] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [task, setTask] = useState("");
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const tick = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [a, t, p] = await Promise.all([api.get("/workforce/time/active"), api.get("/workforce/time/today"), api.get("/workforce/time/productivity")]);
      setActive(a.data.session);
      setToday(t.data);
      setProd(p.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { api.get("/workforce/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ projects: [] })); refresh(); }, [refresh]);

  useEffect(() => {
    if (active) {
      const base = new Date(active.started_at).getTime();
      const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - base) / 1000)));
      update();
      tick.current = setInterval(update, 1000);
      return () => clearInterval(tick.current);
    }
    setElapsed(0);
  }, [active]);

  const projectFromId = (id) => (meta?.projects || []).find((p) => p.id === id);

  const start = async () => {
    setBusy(true);
    const p = projectFromId(projectId);
    try {
      await api.post("/workforce/time/start", { project_id: projectId || null, project_name: p?.name || "General", project_source: p?.source || null, task });
      toast.success("Sesión iniciada");
      setTask("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const switchProject = async () => {
    if (!projectId) return toast.error("Elige el nuevo proyecto");
    await start();
  };

  const stop = async () => {
    setBusy(true);
    try { const r = await api.post("/workforce/time/stop"); toast.success(`Sesión detenida · ${r.data.hours}h registradas en tu parte de horas`); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <OSLayout title="OMNIA Time">
      <div className="space-y-6" data-testid="time-page">
        {/* Live timer */}
        <div className="bg-navy text-cream rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-gradient-violet opacity-30 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-cream/50 text-sm"><Timer className="w-4 h-4" /> {active ? "Sesión en curso" : "Sin sesión activa"}</div>
            <div className="font-editorial text-6xl tracking-tight mt-2 tabular-nums" data-testid="time-clock">{fmtClock(elapsed)}</div>
            {active && <div className="text-cream/70 mt-1" data-testid="time-active-project"><FolderKanban className="w-4 h-4 inline mr-1" /> {active.project_name}{active.task ? ` · ${active.task}` : ""}</div>}

            <div className="mt-6 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-cream/50 mb-1 block">Proyecto</label>
                <select className="w-full rounded-xl bg-cream/10 border border-cream/20 text-cream px-3 py-2.5 text-sm" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="time-project-select">
                  <option value="" className="text-navy">General (sin proyecto)</option>
                  {(meta?.projects || []).map((p) => <option key={p.id} value={p.id} className="text-navy">{p.name} · {p.source}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-cream/50 mb-1 block">Tarea (opcional)</label>
                <input className="w-full rounded-xl bg-cream/10 border border-cream/20 text-cream px-3 py-2.5 text-sm placeholder:text-cream/30" placeholder="¿En qué trabajas?" value={task} onChange={(e) => setTask(e.target.value)} data-testid="time-task-input" />
              </div>
              {!active ? (
                <button onClick={start} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity" data-testid="time-start-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Iniciar
                </button>
              ) : (
                <>
                  <button onClick={switchProject} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-cream/10 text-cream px-5 py-2.5 text-sm hover:bg-cream/20 transition-colors" data-testid="time-switch-btn"><RefreshCw className="w-4 h-4" /> Cambiar proyecto</button>
                  <button onClick={stop} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-destructive text-white px-6 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity" data-testid="time-stop-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />} Detener</button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Productivity */}
        {prod && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="time-productivity">
            {[["Hoy", fmt(prod.today_min)], ["Esta semana", fmt(prod.week_min)], ["Sesiones (sem.)", prod.sessions_week], ["Productividad", `${prod.productividad_pct}%`]].map(([l, v]) => (
              <div key={l} className="bg-white border border-navy/10 rounded-2xl p-4"><div className="text-xs text-navy/40">{l}</div><div className="font-editorial text-2xl mt-1 flex items-center gap-1">{l === "Productividad" && <TrendingUp className="w-4 h-4 text-emerald-500" />}{v}</div></div>
            ))}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {/* By project today */}
          <div className="bg-white border border-navy/10 rounded-2xl p-4">
            <div className="text-sm font-medium mb-3">Tiempo de hoy por proyecto</div>
            {today.by_project.length === 0 ? <div className="text-sm text-navy/40">Aún no registras tiempo hoy.</div>
              : today.by_project.map((p) => (
                <div key={p.project} className="flex items-center justify-between text-sm border-b border-navy/5 py-2">
                  <span className="font-medium">{p.project}</span>
                  <span className="text-navy/50">{fmt(p.minutes)}</span>
                </div>
              ))}
          </div>
          {/* Sessions log */}
          <div className="bg-white border border-navy/10 rounded-2xl p-4">
            <div className="text-sm font-medium mb-3">Sesiones de hoy</div>
            {today.sessions.length === 0 ? <div className="text-sm text-navy/40">Sin sesiones.</div>
              : <div className="space-y-1 max-h-72 overflow-y-auto">{today.sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm border-b border-navy/5 py-2" data-testid={`time-session-${s.id}`}>
                  <div><span className="font-medium">{s.project_name}</span>{s.task && <span className="text-navy/40"> · {s.task}</span>}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "activo" ? "bg-emerald-500/15 text-emerald-600" : "bg-navy/5 text-navy/50"}`}>{s.status === "activo" ? "En curso" : fmt(s.minutes)}</span>
                </div>
              ))}</div>}
          </div>
        </div>
      </div>
    </OSLayout>
  );
}
